/**
 * PortalApi — the driver portal's own HTTP client.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `src/Services/API.js` installs a request interceptor on the DEFAULT axios
 * instance, which attaches the admin bearer token (localStorage SESSION_TOKEN)
 * to every call made through `axios.get/post/...`. The driver portal is a
 * separate surface with a separate principal, so it must never inherit that.
 *
 * `axios.create()` returns an instance with its OWN interceptor managers — the
 * default instance's interceptors are not copied — so nothing registered in
 * API.js can ever run for a portal request. Instance defaults are still merged
 * from `axios.defaults`, so the request interceptor below also explicitly
 * strips any `Authorization` header it did not put there itself. Nothing is
 * ever picked up implicitly: a portal request carries the token this file
 * stored, and no other.
 *
 * SESSION MODEL
 * -------------
 * There are two ways a session gets here, and they behave differently on
 * purpose.
 *
 * A DRIVER OR OPERATOR enrols: a one-time link token is exchanged for an id
 * token (~1h) plus a long-lived refresh token. They should never "log in"
 * again, so:
 *   - the request interceptor refreshes AHEAD of expiry (REFRESH_SKEW_MS), and
 *   - the response interceptor retries a 401 exactly once after a refresh.
 * Concurrent callers share a single in-flight refresh.
 *
 * PICKUP HUNGARY OFFICE STAFF sign in with their DASHBOARD account instead
 * (see `storeStaffSession`). That token comes from the dashboard's own
 * /login + MFA endpoints and belongs to the admin Cognito pool, which
 * PortalCognitoAuthorizer now also accepts; the API turns it into a
 * dispatcher principal, and only if the person holds the Operators
 * permission. Such a session carries no PORTAL refresh token and never
 * calls /portal/refresh — it renews the DASHBOARD's way, through
 * POST /user/renew (see Services/SessionRenewal), where the refresh token
 * stays on the server's Session row and is never handed to a browser. That
 * is what lets a dispatcher stay signed in for up to 30 days while a single
 * sign-out still ends it.
 *
 * Both surfaces keep the SAME token for a staff session: renewal re-points
 * the server's Session row at whatever it issues, so two copies drifting
 * apart would leave one of them unable to renew.
 *
 * The session ENDS (storage cleared, the guard shows the gate) only when the
 * API has said so: it refused the refresh token (any 4xx — a revoked,
 * disabled or expired login), or it answered 401 to a request that could not
 * be refreshed or had already been retried. A network failure or a 5xx says
 * nothing about the login, so it never ends the session — a driver on
 * airport wifi must not need a new link.
 */
import axios from 'axios';
import { putToken } from './BearerToken';
import {
  expiryOf,
  renewSession as renewStaffSession,
  shouldRenewAhead,
} from './SessionRenewal';

// Storage keys. Deliberately distinct from the admin app's `SESSION_TOKEN`.
export const PORTAL_TOKEN_KEY = 'PORTAL_SESSION_TOKEN';
export const PORTAL_REFRESH_TOKEN_KEY = 'PORTAL_REFRESH_TOKEN';
export const PORTAL_EXPIRES_AT_KEY = 'PORTAL_SESSION_EXPIRES_AT';
const PORTAL_PRINCIPAL_KEY = 'PORTAL_PRINCIPAL';

/**
 * Set when this portal session is backed by a DASHBOARD account (office
 * staff on the dispatch board) rather than by an enrolment link.
 *
 * It is what tells the refresh machinery below which door the session came
 * through. A driver's or operator's session has a portal refresh token and
 * uses POST /portal/refresh, exactly as it always did; a staff session has
 * none, and renews through the dashboard's POST /user/renew instead — where
 * the refresh token lives on the server's Session row and is never handed to
 * a browser.
 */
const PORTAL_STAFF_KEY = 'PORTAL_STAFF_SESSION';

/**
 * The driver an administrator is currently acting as, `{ id, name }`.
 *
 * SESSION storage, not local: acting as somebody is a thing you are doing
 * right now, in this tab. Closing the tab ends it, and a second tab is still
 * the office's own board — which is what you want when you open a driver's
 * screen to check something and then get on with your day.
 *
 * The id is the ONLY part the API sees; the name is here so the banner can
 * be drawn before the first response comes back. The API re-states both on
 * every /portal/session, and that is the copy the banner trusts.
 */
const ASSUMED_DRIVER_KEY = 'PORTAL_ASSUMED_DRIVER';

/** The header `dashboardAssumedDriverPrincipal` reads, and the only way in. */
const ASSUME_DRIVER_HEADER = 'x-assume-driver';

// Refresh this long before the id token actually expires. Generous, because a
// driver may open the app on a flaky airport connection and we would rather
// renew early than make them wait through a 401 round-trip.
const REFRESH_SKEW_MS = 5 * 60 * 1000;

// Safari private mode and "block all cookies" both make localStorage throw.
// A driver losing their session is bad, but a white screen is worse.
const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null || value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, String(value));
  } catch {
    // Non-fatal: the session simply won't survive a reload.
  }
};

// sessionStorage throws in exactly the same places localStorage does.
const readSession = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    if (value === null || value === undefined) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, String(value));
  } catch {
    // Non-fatal: the impersonation simply won't survive a reload, and the
    // board comes back — which is the safe way for it to fail.
  }
};

/** The driver this tab is acting as, or null. */
export const getAssumedDriver = () => {
  const raw = readSession(ASSUMED_DRIVER_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Start acting as a driver. Only ever called from the board, and the API
 * refuses it for anyone who is not an administrator, so this storing nothing
 * more than an id is the whole client side of the feature.
 */
export const startAssumingDriver = ({ id, name }) => {
  if (!id) return;
  writeSession(ASSUMED_DRIVER_KEY, JSON.stringify({ id: String(id), name: name || '' }));
  // The cached principal still says 'dispatcher'; the next request will come
  // back as this driver. Drop it so the guard asks /portal/session again
  // rather than bouncing the administrator straight back to the board.
  writeStorage(PORTAL_PRINCIPAL_KEY, null);
};

/** Back to being the office. */
export const stopAssumingDriver = () => {
  writeSession(ASSUMED_DRIVER_KEY, null);
  writeStorage(PORTAL_PRINCIPAL_KEY, null);
};

export const isStaffPortalSession = () => readStorage(PORTAL_STAFF_KEY) === '1';

export const getPortalToken = () => readStorage(PORTAL_TOKEN_KEY);
export const getPortalRefreshToken = () => readStorage(PORTAL_REFRESH_TOKEN_KEY);

const getPortalExpiresAt = () => {
  const raw = readStorage(PORTAL_EXPIRES_AT_KEY);
  const value = Number(raw);
  return raw && Number.isFinite(value) ? value : 0;
};

export const getPortalPrincipal = () => {
  const raw = readStorage(PORTAL_PRINCIPAL_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** A session is "present" as long as we can still mint an id token from it. */
export const hasPortalSession = () => Boolean(getPortalToken() || getPortalRefreshToken());

// --- session-ended notification -------------------------------------------
// The guard subscribes so a definitively dead session flips the UI to the
// "ask for a new code" screen instead of leaving a spinner on screen.
const endedListeners = new Set();

export const onPortalSessionEnded = (listener) => {
  endedListeners.add(listener);
  return () => endedListeners.delete(listener);
};

const notifySessionEnded = () => {
  endedListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A broken listener must not break the request pipeline.
    }
  });
};

export const clearPortalSession = () => {
  writeStorage(PORTAL_TOKEN_KEY, null);
  writeStorage(PORTAL_REFRESH_TOKEN_KEY, null);
  writeStorage(PORTAL_EXPIRES_AT_KEY, null);
  writeStorage(PORTAL_PRINCIPAL_KEY, null);
  writeStorage(PORTAL_STAFF_KEY, null);
  // Whoever signs in next is not the person who was acting as this driver.
  stopAssumingDriver();
};

/** Signs this phone out for good and flips the guard to its gate screen. */
const endPortalSession = () => {
  clearPortalSession();
  notifySessionEnded();
};

/**
 * True when the API refused the refresh token (400/401/403 …): the login is
 * revoked, disabled or expired, and no retry will change that. No response
 * (offline) and a 5xx are not refusals.
 */
const isRefusal = (error) => {
  const status = error?.response?.status;
  return status >= 400 && status < 500;
};

const storeIdToken = (idToken, expiresIn) => {
  if (idToken) writeStorage(PORTAL_TOKEN_KEY, idToken);
  const seconds = Number(expiresIn);
  // Missing or nonsense expires_in: assume the documented ~1h so we still refresh.
  const ttl = Number.isFinite(seconds) && seconds > 0 ? seconds : 3600;
  writeStorage(PORTAL_EXPIRES_AT_KEY, Date.now() + ttl * 1000);
};

/**
 * Store a DASHBOARD id token as the portal's, with the expiry the token
 * itself declares. A dashboard id token lives one day; `storeIdToken`'s
 * fallback assumes the portal pool's hour, which would have the portal
 * believing a good token was nearly spent.
 */
const storeStaffIdToken = (idToken) => {
  const remainingMs = expiryOf(idToken) - Date.now();
  storeIdToken(idToken, remainingMs > 0 ? Math.floor(remainingMs / 1000) : undefined);
};

const storePortalPrincipal = (principal) => {
  writeStorage(PORTAL_PRINCIPAL_KEY, JSON.stringify(principal));
};

/** Persists everything `POST /portal/enrol` hands back. */
const storePortalSession = (session) => {
  writeStorage(PORTAL_REFRESH_TOKEN_KEY, session.refresh_token);
  storeIdToken(session.id_token, session.expires_in);
  storePortalPrincipal(session.principal);
};

const buildBaseUrl = () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * The API wraps every JSON response as `{ success: true, data: <payload> }`
 * (SuccessResponseMiddleware in ph-core). Unwrap it in exactly one place so
 * each endpoint reads the payload. A body that isn't enveloped passes
 * through untouched.
 *
 * Getting this wrong is silent and delayed: enrolment would store the
 * wrapper instead of the tokens, and the first silent refresh — an hour
 * later — would store `undefined` and sign the driver out.
 */
export const unwrapApi = (body) => (
  body && typeof body === 'object' && body.success === true && 'data' in body
    ? body.data
    : body
);

/** The payload of one portal response (shared with OperatorPortalApi.js). */
export const payloadOf = ({ data }) => unwrapApi(data);

/**
 * The portal's own axios instance. Never use the bare `axios` export here —
 * that one carries the admin interceptor from API.js.
 */
export const portalClient = axios.create({
  baseURL: buildBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

const setAuthHeader = (config, token) => {
  const next = config;
  next.headers = next.headers || {};
  next.headers.Authorization = `Bearer ${token}`;
};

const stripAuthHeader = (config) => {
  const next = config;
  if (next.headers) delete next.headers.Authorization;
};

const shouldRefreshAhead = () => {
  // A staff session renews through the dashboard, on the dashboard's own
  // schedule (SessionRenewal reads the token's `exp`), so the portal's
  // skew-based check does not apply to it.
  if (isStaffPortalSession()) return shouldRenewAhead();
  if (!getPortalRefreshToken()) return false;
  if (!getPortalToken()) return true;
  const expiresAt = getPortalExpiresAt();
  // Unknown expiry: leave it alone and let a 401 drive the refresh.
  if (!expiresAt) return false;
  return expiresAt - Date.now() <= REFRESH_SKEW_MS;
};

let refreshInFlight = null;

// Enrolment tokens are single use, so the exchange must happen once per token
// however many times a screen mounts. React's development mode mounts twice on
// purpose, and a remount in production would otherwise burn the token and
// leave the driver on a spinner. Every caller for the same token shares one
// request and one result.
const enrolments = new Map();

/**
 * Exchanges the refresh token for a fresh id token. Concurrent callers share
 * one request, so a screen firing three calls at once refreshes once.
 */
export const refreshPortalSession = () => {
  if (refreshInFlight) return refreshInFlight;

  // Office staff: the same renewal the dashboard uses, so one session is
  // kept alive in one place. It swaps the token on the dashboard's store,
  // and the portal's copy is updated from it — both surfaces then carry the
  // token the server's Session row now points at.
  if (isStaffPortalSession()) {
    refreshInFlight = renewStaffSession()
      .then((idToken) => {
        storeStaffIdToken(idToken);
        return idToken;
      })
      .finally(() => {
        refreshInFlight = null;
      });

    return refreshInFlight;
  }

  const refreshToken = getPortalRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No portal refresh token'));
  }

  refreshInFlight = portalClient
    .post(
      '/portal/refresh',
      { refresh_token: refreshToken },
      // Marked so neither interceptor touches it: no stale id token attached,
      // and a failing refresh must never recurse into another refresh.
      { skipPortalAuth: true },
    )
    .then((response) => {
      const payload = payloadOf(response);
      storeIdToken(payload.id_token, payload.expires_in);
      return payload.id_token;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

/**
 * Refreshes only if the id token is missing or about to expire. Safe to call
 * on a timer or when the app comes back to the foreground; resolves to the
 * usable id token, or null when it could not be renewed. Only a refusal ends
 * the session — offline, the next tick simply tries again.
 */
export const ensurePortalToken = async () => {
  if (!shouldRefreshAhead()) return getPortalToken();
  try {
    return await refreshPortalSession();
  } catch (error) {
    if (isRefusal(error)) endPortalSession();
    return null;
  }
};

/** Test seam — drops any in-flight refresh so specs start from a clean slate. */
export const resetPortalRefreshState = () => {
  refreshInFlight = null;
  enrolments.clear();
};

portalClient.interceptors.request.use(async (config) => {
  const next = config;

  if (next.skipPortalAuth) {
    stripAuthHeader(next);
    return next;
  }

  // Pre-emptive (silent) refresh. Skipped on a retry, where the response
  // interceptor has already refreshed for us.
  if (!next.portalRetried && shouldRefreshAhead()) {
    try {
      await refreshPortalSession();
    } catch (error) {
      // Refused: the login is gone. End it here instead of sending a request
      // that can only 401 and refresh a second time.
      if (isRefusal(error)) {
        endPortalSession();
        throw error;
      }
      // Offline or a server fault: go ahead with the token we have.
    }
  }

  // Acting as a driver, if this tab is. Set here rather than per call so it
  // cannot be forgotten on one of them — a page that read a driver's trips
  // as the driver and then wrote as the office would be the worst of both.
  const assumed = getAssumedDriver();
  if (assumed) next.headers = { ...next.headers, [ASSUME_DRIVER_HEADER]: assumed.id };

  const token = getPortalToken();
  if (token) setAuthHeader(next, token);
  // Belt and braces: an instance default (or a future global) must never put
  // the ADMIN token on a portal request.
  else stripAuthHeader(next);

  return next;
});

portalClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    // Only a 401 on a request the portal token signed says anything about
    // the session. Enrolment and the refresh itself (`skipPortalAuth`) carry
    // no portal token: their refusals are handled where they are made.
    if (error?.response?.status !== 401 || !config || config.skipPortalAuth) throw error;

    // Refused again straight after a successful refresh: the API no longer
    // accepts this login (revoked, disabled). Another refresh would loop.
    if (config.portalRetried) {
      endPortalSession();
      throw error;
    }

    // A staff session has no portal refresh token and does not need one —
    // it renews through the dashboard. Anything else with no refresh token
    // has nothing left to try.
    if (!getPortalRefreshToken() && !isStaffPortalSession()) {
      endPortalSession();
      throw error;
    }

    // Exactly one refresh-and-retry per request.
    config.portalRetried = true;

    try {
      await refreshPortalSession();
    } catch (refreshError) {
      // Refused: the refresh token is dead too — this driver needs a new
      // enrolment link. Anything else (offline, a 5xx) keeps the session,
      // and the screen reports that failure rather than a stale 401.
      if (!isRefusal(refreshError)) throw refreshError;
      endPortalSession();
      throw error;
    }

    return portalClient(config);
  },
);

// --- endpoints -------------------------------------------------------------

/** Exchanges the one-time enrolment token from the QR/link for a session. */
export const enrolPortal = (token) => {
  if (!enrolments.has(token)) {
    const exchange = (async () => {
      const payload = payloadOf(await portalClient.post(
        '/portal/enrol',
        { token },
        { skipPortalAuth: true },
      ));
      storePortalSession(payload);
      return payload;
    })();
    // A network failure never reached the server, so a retry may re-send. A
    // refusal (spent, expired, unknown token) is final and stays cached.
    exchange.catch((error) => {
      if (!error?.response) enrolments.delete(token);
    });
    enrolments.set(token, exchange);
  }
  return enrolments.get(token);
};

/** Who this phone is signed in as — the API's session view, cached for the next cold start. */
export const fetchPortalSession = async () => {
  // Who the answer will be about, read BEFORE asking: an administrator can
  // press "Open their screen" while this request is in the air, and the
  // answer that then comes back describes the person they have stopped
  // being. Caching it would make the next screen's first paint the
  // dispatcher on a driver's day. The caller still gets its own answer —
  // only the cache is withheld, because a cache is for next time.
  const asked = getAssumedDriver()?.id || '';
  const principal = payloadOf(await portalClient.get('/portal/session'));
  if ((getAssumedDriver()?.id || '') === asked) storePortalPrincipal(principal);
  return principal;
};

/**
 * Sign office staff in with the DASHBOARD id token they just obtained from
 * /login (and its MFA challenge) — the portal's only password-backed way in,
 * and the only one for a dispatcher.
 *
 * Deliberately stores NO refresh token. A dashboard sign-in mints an id
 * token and nothing else, and inventing a portal refresh for it would mean a
 * second way to keep an office session alive that nobody could revoke from
 * the dashboard. Without one, every refresh path here is already inert and
 * the session lasts exactly as long as the dashboard's own.
 *
 * The permission check is the API's, so this asks for it before claiming
 * anything: the token is stored, `/portal/session` is called with it, and a
 * refusal clears it again and is thrown for the caller to show. That way a
 * colleague without the Operators permission is told so on the sign-in form
 * rather than being dropped onto a board that cannot load.
 *
 * @param idToken the dashboard id token (AuthController stored it as SESSION_TOKEN)
 * @returns the portal principal — `kind: 'dispatcher'`
 */
export const storeStaffSession = async (idToken) => {
  if (!idToken) throw new Error('No dashboard token to sign in with');

  clearPortalSession();
  // The token's own `exp` — a dashboard id token lives a DAY, not the hour
  // storeIdToken would otherwise assume.
  storeStaffIdToken(idToken);
  // Marks which door this session came through, so the refresh machinery
  // renews it the dashboard's way rather than looking for a portal refresh
  // token it will never have.
  writeStorage(PORTAL_STAFF_KEY, '1');
  // The dashboard's store gets the same token, because it IS the same
  // session: renewal re-points the server's Session row at whatever it
  // issues, so two surfaces holding two different tokens for one row would
  // leave the loser unable to renew.
  putToken(idToken);

  try {
    return await fetchPortalSession();
  } catch (error) {
    // Never leave a half-session behind: the next screen would read a token
    // the API has already refused.
    clearPortalSession();
    throw error;
  }
};

/**
 * Trips for a date window. `from`/`to` are YYYY-MM-DD Budapest dates.
 * The API already scrubs every commercial field before it reaches a driver.
 */
export const fetchPortalTrips = async ({ from, to } = {}) => (
  payloadOf(await portalClient.get('/portal/trips', { params: { from, to } })).trips
);

/**
 * Records one step of the trip as the driver lives it. `event` is one of
 * departed_to_pickup | arrived_at_pickup | departed_to_dropoff |
 * arrived_at_dropoff | no_show | overtime_started | overtime_ended |
 * overtime_reported | passenger_call_attempted.
 *   - overtime_started carries `stage` ('pickup' at state 2, 'dropoff' at 3);
 *   - overtime_reported carries `minutes` (1–600), an optional `note` and
 *     where it happened, `stage` ('pickup' | 'dropoff' — the API's default
 *     is 'dropoff');
 *   - no_show carries `no_show_evidence` — the ticked checkpoints, the
 *     driver's note, whether the office agreed it and any photos — and the
 *     API REFUSES it without them (NoShowSheet collects them);
 *   - passenger_call_attempted carries nothing but the position, and is
 *     sent when the driver taps the call button on the customer, so a
 *     later no-show can say a call was really tried.
 * `lat`/`lon` are only sent when the phone gave us a fix. Anything not given
 * is left out of the body rather than sent as null.
 * Resolves to the updated (scrubbed) trip.
 */
export const sendPortalTripEvent = async ({
  // eslint-disable-next-line camelcase
  booking_id, event, lat, lon, stage, minutes, note,
  // eslint-disable-next-line camelcase
  no_show_evidence: noShowEvidence, name_sign: nameSign, contact_method: contactMethod,
}) => {
  // eslint-disable-next-line camelcase
  const body = { booking_id, event };
  if (stage) body.stage = stage;
  if (minutes !== undefined && minutes !== null && minutes !== '') body.minutes = Number(minutes);
  if (typeof note === 'string' && note.trim()) body.note = note.trim();
  if (noShowEvidence) body.no_show_evidence = noShowEvidence;
  if (nameSign) body.name_sign = nameSign;
  if (contactMethod) body.contact_method = contactMethod;
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    body.lat = lat;
    body.lon = lon;
  }
  return payloadOf(await portalClient.post('/portal/trip-event', body));
};

/**
 * Reads a receipt photo — the total, the date and the merchant — and says
 * whether this photo has been filed before.
 *
 * CALLED AS SOON AS THE PHOTO IS PICKED, never on submit. The whole point is
 * to PREFILL the amount, and a reading that arrives after the driver has
 * pressed Beküldés arrives too late to be of use to anybody.
 *
 * `photo` is imageDownscale's result; the same `{ name, type, data }` the
 * submit sends, so the hash the API computes here is the hash of the bytes
 * that will be stored.
 *
 * Resolves to
 *   `{ photo_sha256, readable, read, unreadable_reasons, duplicate_of }`
 * where `read` is `{ total, currency, occurred_at, merchant,
 * total_source_line }` — EVERY field of it independently nullable, including
 * `read` itself — and `unreadable_reasons` is a closed vocabulary from the
 * API. `readable: false` always comes with `read: null`.
 *
 * NOTHING FROM HERE IS SENT BACK ON SUBMIT. `createPortalExpense` keeps the
 * request shape it always had: the server recomputes the hash from the bytes
 * it receives and looks this reading up itself. A client-supplied total would
 * then be vouched for by a dispatcher-facing badge, which is exactly the
 * thing a forged reading would want.
 *
 * Throws like every other call here (a 500, a dropped connection). The caller
 * must treat a failure as an UNREADABLE photo — see ExpenseSheet, which is
 * the one place that mapping lives.
 */
export const scanPortalExpensePhoto = async (photo) => payloadOf(await portalClient.post(
  '/portal/expenses/scan',
  { photo: { name: photo.name, type: photo.type, data: photo.data } },
));

/**
 * Submits an expense the driver paid out of pocket (parking ticket, etc.).
 * `photo` is imageDownscale's result; only `{ name, type, data }` travels,
 * `data` as bare base64 — no `data:` prefix. The caller is responsible for
 * downscaling it first: the API sits behind a Lambda with a 6 MB payload
 * ceiling. A blank note and a missing booking are left out of the body.
 * Resolves to the stored claim.
 */
export const createPortalExpense = async ({
  // eslint-disable-next-line camelcase
  type, amount, currency, note, booking_id, photo,
}) => {
  const body = {
    type,
    amount,
    currency,
    photo: { name: photo.name, type: photo.type, data: photo.data },
  };
  const trimmed = (note || '').trim();
  if (trimmed) body.note = trimmed;
  // eslint-disable-next-line camelcase
  if (booking_id) body.booking_id = booking_id;
  return payloadOf(await portalClient.post('/portal/expenses', body));
};

/**
 * The caller's own submitted expenses, newest first: a driver's own, or an
 * operator login's operator's. `bookingId` narrows them to one trip (the
 * operator's trip details); the API still scopes by the signed-in login.
 */
export const fetchPortalExpenses = async ({ bookingId } = {}) => (
  payloadOf(await portalClient.get(
    '/portal/expenses',
    bookingId ? { params: { booking_id: bookingId } } : undefined,
  )).expenses
);

// --- phone notifications ---------------------------------------------------
// The driver's own phones. Driver logins only: the API refuses an operator
// or dispatcher login, so the portal only offers this on a driver screen.

/**
 * The VAPID public key plus the phones this driver has registered.
 * `public_key: null` means push is not configured on this deploy — the
 * portal says so rather than showing a switch that would do nothing.
 */
export const fetchPortalPushState = async () => payloadOf(await portalClient.get('/portal/push'));

/** Registers this browser. `subscription` is `{ endpoint, keys: { p256dh, auth } }`. */
export const subscribePortalPush = async (subscription) => (
  payloadOf(await portalClient.post('/portal/push', subscription))
);

/** Forgets one registered phone. An id that is not this driver's answers `{ removed: false }`. */
export const unsubscribePortalPush = async (deviceId) => (
  payloadOf(await portalClient.delete('/portal/push', { params: { id: deviceId } }))
);

/**
 * The three calls `Services/PushNotifications` needs, for a driver. Passing
 * this rather than the module keeps that file unaware of which surface it is
 * running on.
 */
export const driverPushApi = {
  getState: fetchPortalPushState,
  subscribe: subscribePortalPush,
  unsubscribe: unsubscribePortalPush,
};
